/**
 * DatePicker
 */

import { memo } from 'react';
import dayjs, { Dayjs } from 'dayjs';

import { TextField } from '@mui/material';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers';

import { DatePickerType, OnChangeType } from 'app/components/DatePicker/types';

// x-date-pickers v6 removed `renderInput`. The input is now supplied through
// `slots.textField` (which component to render) and `slotProps.textField` (the
// props to give it). Nothing about this component's own API changed, so the ~18
// call sites are untouched.
//
// `renderInput` was removed in v6 and is NOT shimmed here. v6 ignored it silently;
// v9 goes further - the textField slot must be a PickersTextField built on
// PickersSectionList, so handing it a plain <TextField> throws at runtime
// ("The `sectionListRef` prop has not been initialized"). No caller in this
// codebase passes one, so a shim could only ever cause that crash. It is gone.

// Guard on isValid(). A half-typed date is still a Dayjs object, so `date &&
// date.format()` yields the literal string "Invalid Date" and hands that to the
// form as if it were a value. That was always true, but v5 only fired onChange
// once the field was left, whereas the v9 accessible field fires on every
// keystroke - typing one date emits three invalid values before the real one.
// Partial input is an absent value, so emit null and let the form's own required
// / range rules speak.
const defaultOnchange =
  (onChange: OnChangeType) => (date: Dayjs | null, context?: unknown) => {
    if (onChange) {
      return onChange(date?.isValid() ? date.format() : null, context);
    }
  };

// v6 validates `value`, `minDate` and `maxDate` as real Dayjs objects - it calls
// value.isBefore() internally. v5 tolerated the strings this app's forms hold; v6
// throws "value.isBefore is not a function". Converting here keeps every call site
// passing strings exactly as before.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const toDayjs = (v: any): Dayjs | null =>
  v === null || v === undefined || v === '' ? null : dayjs(v);

export const DatePicker = ({
  onChange,
  error,
  helperText,
  placeholder,
  inputFormat,
  toolbarPlaceholder,
  value,
  minDate,
  maxDate,
  inputProps,
  ...props
}: DatePickerType) => (
  <MuiDatePicker
    value={toDayjs(value)}
    {...(minDate ? { minDate: toDayjs(minDate) ?? undefined } : {})}
    {...(maxDate ? { maxDate: toDayjs(maxDate) ?? undefined } : {})}
    // v6 renamed `inputFormat` to `format`. Mapped here rather than at the call
    // sites, which still pass inputFormat.
    {...(inputFormat ? { format: inputFormat } : {})}
    {...props}
    slotProps={{
      textField: {
        error,
        helperText,
        // MERGE, do not replace. GenerateField passes
        // inputProps={{ 'aria-label': name }}, and in v6 root-level inputProps are
        // not a picker prop - everything has to come through this slot. Setting a
        // fresh object here silently dropped the aria-label on every date field.
        // v9 route for raw <input> attributes. The picker's text field no longer
        // accepts `inputProps` at all - it is a compile error, not a silent drop -
        // and this is where the aria-label from Form/GenerateField travels.
        slotProps: {
          htmlInput: {
            ...inputProps,
            placeholder: placeholder ?? 'MMM DD YYYY',
          },
        },
      },
      // v6 moved toolbarPlaceholder off the root props and onto the toolbar slot.
      // Left as a top-level prop it would be accepted and silently dropped.
      toolbar: {
        toolbarPlaceholder: toolbarPlaceholder ?? 'MM/DD/YYYY',
      },
    }}
    onChange={defaultOnchange(onChange)}
  />
);

export default memo(DatePicker);
